import type { JSX } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useSession, useDescope } from '@descope/react-sdk';
import { ClinicalAppPermission } from '@packages/core-shared';
import { useAuth, usePermissions, useSessionClaims } from '../hooks';
import { useFirebaseSync } from '../providers';
import { Button, Spinner } from '@percihealth/react';

interface Props {
  loginPath?: string;
  askForRightsPath?: string;
  /**
   * The Descope permission the route needs. `crm_access` - being allowed into
   * EBP at all - is what almost every route wants; the referral sites ask for
   * the referrer permission instead.
   */
  permission?: ClinicalAppPermission;
  children: JSX.Element;
}

export const PrivateElement = ({
  children,
  loginPath = '/login',
  askForRightsPath = '/ask-for-rights',
  permission = ClinicalAppPermission.CrmAccess,
}: Props): JSX.Element => {
  // Two sessions settle independently: the Descope session (the credential and
  // the permissions) and the Firebase session (Firestore/Storage
  // subscriptions), which DescopeSessionProvider keeps in step and reports on.
  const { user, isInitializing } = useAuth();
  const { isSessionLoading, isAuthenticated } = useSession();
  const { status: firebaseSync, retry } = useFirebaseSync();
  const permissions = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const descope = useDescope();

  const descopeEmail = useSessionClaims()?.email;

  const centered = (child: JSX.Element) => <div style={{ alignSelf: 'center' }}>{child}</div>;

  // Either session is still settling (restoring from storage on a reload):
  // wait rather than decide access on half-loaded state.
  if (isInitializing || isSessionLoading) {
    return centered(<Spinner />);
  }

  // No Descope session at all: genuinely signed out. This is the ONLY path to
  // the login page - a stale Firebase user alone is not, because it may belong
  // to a session that is being torn down or replaced.
  if (!isAuthenticated) {
    (globalThis as any).firstUrl = location;
    return <Navigate to={loginPath} />;
  }

  // The Firebase subscription session could not be brought in step with the
  // Descope one. Fail closed - do not render protected content and its live
  // reads on a mismatched session - and offer a retry.
  if (firebaseSync === 'error') {
    // A persistent exchange failure (a missing email claim, a backend or CORS
    // problem) would otherwise trap a persisted session on this screen every
    // reload with no way out - the header's sign-out is not on this screen, and
    // the referral layouts have none. Offer a sign-out that clears the Descope
    // session and returns to /login, alongside the retry.
    return centered(
      <div style={{ display: 'grid', gap: 12, justifyItems: 'center' }}>
        <p>We could not sync your session. Try again, or sign out.</p>
        <Button level='outline' onClick={retry}>
          Try again
        </Button>
        <Button
          level='quiet'
          onClick={async () => {
            // Navigate only if the Descope session was actually cleared:
            // otherwise /login (which redirects an authenticated session
            // straight back) would bounce the user into this screen again.
            try {
              await descope.logout();
            } catch {
              return;
            }
            navigate(loginPath);
          }}
        >
          Sign out
        </Button>
      </div>,
    );
  }

  // Descope is authenticated but Firebase is not yet in step with it (initial
  // exchange, a reload where Descope restored first, an account switch, or a
  // permission re-mint): wait, so nothing renders against a not-yet-current
  // session.
  if (firebaseSync !== 'ready') {
    return centered(<Spinner />);
  }

  // AuthProvider resolves `useAuth().user` asynchronously (getIdTokenResult),
  // so after an account switch it can still hold the previous account for a
  // moment even once the sync reports ready. Wait until it reflects the
  // current Descope account, so components below that read `useAuth().user`
  // (e.g. note authorship) never see the previous identity.
  if (typeof descopeEmail === 'string' && user?.email !== descopeEmail) {
    return centered(<Spinner />);
  }

  if (!permissions.includes(permission)) {
    return <Navigate to={askForRightsPath} />;
  }

  return children;
};
